import {
  autoPlacement,
  autoUpdate,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import cn from 'classnames';
import { FC, useState } from 'react';
import { createPortal } from 'react-dom';

import Text from 'components/atoms/text';
import {
  FloatingTooltipProps,
  FloatingTooltipTextProps,
} from 'components/molecules/tooltipFloating/types';

import sm from './styles.module.scss';

function FloatingTooltip({
  children,
  useDefaultWidthAndHeight,
  disabled,
  allowedPlacements,
  tooltipChildren,
  wrapperClasses,
}: FloatingTooltipProps) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      autoPlacement({ allowedPlacements: allowedPlacements }),
      shift({ padding: 8 }),
    ],
  });

  const hover = useHover(context, {
    enabled: !disabled,
    handleClose: safePolygon(),
    delay: { open: 500, close: 100 },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className={cn(
          {
            [sm.FullWidthAndHeight]: !useDefaultWidthAndHeight,
          },
          wrapperClasses ? wrapperClasses : ''
        )}
      >
        {children}
      </span>

      {!disabled &&
        open &&
        createPortal(
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className={sm.Tooltip}
            style={floatingStyles}
          >
            {tooltipChildren}
          </div>,
          document.body
        )}
    </>
  );
}

export const FloatingTooltipText: FC<FloatingTooltipTextProps> = ({
  title,
  wrapperClasses,
  useDefaultWidthAndHeight,
  disabled,
  allowedPlacements,
  children,
}) => (
  <FloatingTooltip
    children={children}
    tooltipChildren={<Text content={title} />}
    useDefaultWidthAndHeight={useDefaultWidthAndHeight}
    disabled={disabled}
    allowedPlacements={allowedPlacements}
    wrapperClasses={wrapperClasses}
  />
);

export default FloatingTooltip;
